import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { safeApiCall, type ApiDegradeResult } from './apiConnectivity'
import { validateOperation } from './subscriptionGuard'
import { bytesToGiB } from '../utils/storageUnits'
import type {
  QueuedStorageUpload,
  StorageConnectionMetadata,
  StorageFileVisibility,
  WorkspaceFileMetadata,
  WorkspaceStorageQuota,
} from '../types/schema'

export const STORAGE_MAX_FILE_BYTES = 50 * 1024 * 1024
export const STORAGE_MAX_BATCH_FILES = 10
export const STORAGE_MAX_BATCH_BYTES = 100 * 1024 * 1024

const callable = <Input, Output>(name: string) => httpsCallable<Input, Output>(firebaseFunctions, name)

const timestampMillis = (value: unknown) => {
  if (value && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function') return value.toMillis()
  return 0
}

const mapFile = (snapshot: QueryDocumentSnapshot<DocumentData>): WorkspaceFileMetadata => ({
  id: snapshot.id,
  ...snapshot.data(),
}) as WorkspaceFileMetadata

const mapConnection = (snapshot: QueryDocumentSnapshot<DocumentData>): StorageConnectionMetadata => ({
  id: snapshot.id,
  ...snapshot.data(),
}) as StorageConnectionMetadata

const mapQueued = (snapshot: QueryDocumentSnapshot<DocumentData>): QueuedStorageUpload => ({
  id: snapshot.id,
  ...snapshot.data(),
}) as QueuedStorageUpload

export const sha256File = async (file: Blob) => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

const validateBatch = (files: File[]) => {
  if (files.length === 0) throw new Error('Choose at least one file.')
  if (files.length > STORAGE_MAX_BATCH_FILES) throw new Error('A batch can include up to 10 files.')
  const total = files.reduce((sum, file) => sum + file.size, 0)
  if (total > STORAGE_MAX_BATCH_BYTES) throw new Error('A batch cannot exceed 100 MB.')
  const oversized = files.find((file) => file.size > STORAGE_MAX_FILE_BYTES)
  if (oversized) throw new Error(`${oversized.name} exceeds the 50 MB file limit.`)
}

export const uploadWorkspaceFiles = async (input: {
  workspaceId: string
  files: File[]
  visibility: StorageFileVisibility
  scheduledAt?: string | null
  artifactType?: string
  artifactId?: string | null
  sourceChatTitle?: string | null
  originalAttachmentType?: string | null
  source?: 'ITEM_ATTACHMENT' | string
  onProgress?: (completed: number, total: number, fileName: string) => void
}) => {
  validateBatch(input.files)
  const results: Array<{ fileId?: string; reservationId: string; status: string; objectPath?: string; downloadUrl?: string }> = []
  for (let index = 0; index < input.files.length; index += 1) {
    const file = input.files[index]
    const sizeGiB = bytesToGiB(file.size)
    let reservationId: string | null = null
    try {
      if (input.artifactType !== 'EDU_HOMEWORK' && input.artifactType !== 'EDU_PRACTICE_LOG' && input.artifactType !== 'EDU_ANNOUNCEMENT' && input.artifactType !== 'EDU_MESSAGE' && input.artifactType !== 'EDU_FEED_POST' && input.artifactType !== 'EDU_EVENT') {
        await runTransaction(firestoreDb, (transaction) => validateOperation(transaction, input.workspaceId, 'storageGB', sizeGiB))
      }
      const checksumSha256 = await sha256File(file)
      const reservation = await callable<{
      workspaceId: string
      fileName: string
      mimeType: string
      sizeBytes: number
      checksumSha256: string
      visibility: StorageFileVisibility
      scheduledAt?: string | null
      artifactType?: string
      artifactId?: string | null
      sourceChatTitle?: string | null
      originalAttachmentType?: string | null
      }, { reservationId: string; uploadUrl: string }>('issueStorageUploadUrl')({
        workspaceId: input.workspaceId,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        checksumSha256,
        visibility: input.visibility,
        scheduledAt: input.scheduledAt,
        artifactType: input.artifactType,
        artifactId: input.artifactId,
        sourceChatTitle: input.sourceChatTitle,
        originalAttachmentType: input.originalAttachmentType ?? file.type,
      })
      reservationId = reservation.data.reservationId
      const upload = await fetch(reservation.data.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      })
      if (!upload.ok) throw new Error(`Upload failed for ${file.name} (${upload.status}).`)
      const finalized = await callable<{ reservationId: string }, { fileId?: string; reservationId: string; status: string; objectPath?: string; downloadUrl?: string }>('finalizeStorageUpload')({ reservationId })
      results.push(finalized.data)
      input.onProgress?.(index + 1, input.files.length, file.name)
    } catch (error) {
      if (reservationId) {
        await callable<{ reservationId: string }, { released: boolean }>('releaseStorageUploadReservation')({ reservationId }).catch(() => undefined)
      }
      const details = error && typeof error === 'object' && 'details' in error ? (error as { details?: Record<string, unknown> }).details : undefined
      if ((error instanceof Error && error.message.includes('PAYWALL_REQUIRED')) || details?.code === 'PAYWALL_REQUIRED') {
        window.dispatchEvent(new CustomEvent('linkhub:paywall-required', { detail: { workspaceId: input.workspaceId, featureKey: 'storageGB', currentUsage: details?.currentUsage, limit: details?.limit } }))
      }
      throw error
    }
  }
  return results
}

export const uploadWorkspaceArtifact = async (input: {
  workspaceId: string
  file: File
  visibility?: StorageFileVisibility
  artifactType: string
  artifactId: string
}) => {
  const [result] = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: [input.file],
    visibility: input.visibility ?? 'WORKSPACE_SHARED',
    artifactType: input.artifactType,
    artifactId: input.artifactId,
  })
  if (!result.fileId) throw new Error('Artifact upload did not produce a file record.')
  if (!result.downloadUrl || !result.objectPath) throw new Error('Artifact upload did not return a download reference.')
  return { fileId: result.fileId, downloadUrl: result.downloadUrl, storagePath: result.objectPath }
}

const quotaRequests = new Map<string, Promise<ApiDegradeResult<WorkspaceStorageQuota>>>()

export const getWorkspaceStorageQuota = (workspaceId: string) => {
  const existing = quotaRequests.get(workspaceId)
  if (existing) return existing
  const request = safeApiCall(async () => {
    const result = await callable<{ workspaceId: string }, WorkspaceStorageQuota>('checkStorageQuota')({ workspaceId })
    return result.data
  })
  quotaRequests.set(workspaceId, request)
  globalThis.setTimeout(() => quotaRequests.delete(workspaceId), 5_000)
  return request
}

export const subscribeWorkspaceFiles = (
  workspaceId: string,
  userId: string,
  isAdmin: boolean,
  onChange: (files: WorkspaceFileMetadata[]) => void,
  onError?: (error: Error) => void,
) => {
  void isAdmin
  let shared: WorkspaceFileMetadata[] = []
  let own: WorkspaceFileMetadata[] = []
  let ownAiMagic: WorkspaceFileMetadata[] = []
  const publish = () => onChange(sortStorageFilesNewestFirst(Array.from(new Map([...shared, ...own, ...ownAiMagic].map((file) => [file.id, file])).values())))
  const stopShared = onSnapshot(
    query(
      collection(firestoreDb, 'file_metadata'),
      where('workspaceId', '==', workspaceId),
      where('visibility', '==', 'WORKSPACE_SHARED'),
      where('artifactType', 'not-in', ['AI_MAGIC_UPLOAD']),
      orderBy('artifactType'),
      orderBy('createdAt', 'desc'),
      limit(500),
    ),
    (snapshot) => { shared = snapshot.docs.map(mapFile); publish() },
    (error) => onError?.(error),
  )
  const stopOwn = onSnapshot(
    query(collection(firestoreDb, 'file_metadata'), where('workspaceId', '==', workspaceId), where('uploaderId', '==', userId), orderBy('createdAt', 'desc'), limit(500)),
    (snapshot) => { own = snapshot.docs.map(mapFile); publish() },
    (error) => onError?.(error),
  )
  // AI Magic uploads are private and have a dedicated Upload Hub lane. Keep a
  // focused uploader-owned subscription so they still appear even if the
  // generic uploader feed hits its 500-row cap.
  const stopOwnAiMagic = onSnapshot(
    query(
      collection(firestoreDb, 'file_metadata'),
      where('workspaceId', '==', workspaceId),
      where('uploaderId', '==', userId),
      where('artifactType', '==', 'AI_MAGIC_UPLOAD'),
    ),
    (snapshot) => { ownAiMagic = snapshot.docs.map(mapFile); publish() },
    (error) => onError?.(error),
  )
  return () => { stopShared(); stopOwn(); stopOwnAiMagic() }
}

export const subscribeStorageConnections = (workspaceId: string, onChange: (connections: StorageConnectionMetadata[]) => void, onError?: (error: Error) => void) => onSnapshot(
  query(collection(firestoreDb, 'storage_connections'), where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc')),
  (snapshot) => onChange(snapshot.docs.map(mapConnection)),
  (error) => onError?.(error),
)

export const subscribeQueuedStorageUploads = (workspaceId: string, userId: string, isAdmin: boolean, onChange: (uploads: QueuedStorageUpload[]) => void, onError?: (error: Error) => void) => onSnapshot(
  isAdmin
    ? query(collection(firestoreDb, 'queued_uploads'), where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'), limit(100))
    : query(collection(firestoreDb, 'queued_uploads'), where('workspaceId', '==', workspaceId), where('uploaderId', '==', userId), orderBy('createdAt', 'desc'), limit(100)),
  (snapshot) => onChange(snapshot.docs.map(mapQueued).filter((entry) => ['RESERVED', 'SCHEDULED'].includes(entry.status))),
  (error) => onError?.(error),
)

export const openStorageFile = async (fileId: string) => {
  window.open(await getStorageFileUrl(fileId), '_blank', 'noopener,noreferrer')
}

export const getStorageFileUrl = async (fileId: string) =>
  (await callable<{ fileId: string }, { url: string }>('getStorageFileOpenUrl')({ fileId })).data.url

// Callable-generated URLs are signed and expire; cache them for ~10 min so
// thumbnails + preview repeatedly rendering the same file don't re-fetch.
const storageUrlCache = new Map<string, { url: string; expiresAt: number; inflight?: Promise<string> }>()
const STORAGE_URL_TTL_MS = 10 * 60 * 1000

export const getCachedStorageFileUrl = (fileId: string): Promise<string> => {
  const now = Date.now()
  const cached = storageUrlCache.get(fileId)
  if (cached && cached.expiresAt > now) return Promise.resolve(cached.url)
  if (cached?.inflight) return cached.inflight
  const inflight = getStorageFileUrl(fileId)
    .then((url) => {
      storageUrlCache.set(fileId, { url, expiresAt: Date.now() + STORAGE_URL_TTL_MS })
      return url
    })
    .catch((error) => {
      storageUrlCache.delete(fileId)
      throw error
    })
  storageUrlCache.set(fileId, { url: cached?.url ?? '', expiresAt: 0, inflight })
  return inflight
}

export const updateStorageVisibility = async (fileId: string, visibility: StorageFileVisibility) => {
  await callable<{ fileId: string; visibility: StorageFileVisibility }, { visibility: StorageFileVisibility }>('updateStorageFileVisibility')({ fileId, visibility })
}

export const deleteWorkspaceFile = async (fileId: string, reason: string) => {
  await callable<{ fileId: string; reason: string }, { deleted: boolean }>('deleteStorageFile')({ fileId, reason })
}

export const cancelQueuedUpload = async (reservationId: string) => {
  await callable<{ reservationId: string }, { cancelled: boolean }>('cancelScheduledStorageUpload')({ reservationId })
}

export const activateWorkspaceStorageBurst = async (workspaceId: string) => {
  const result = await callable<{ workspaceId: string }, { burstBytes: number; expiresAt: string }>('activateStorageBurst')({ workspaceId })
  return result.data
}

export const startGoogleDriveOAuth = async (workspaceId: string) => {
  const result = await callable<{ workspaceId: string; provider: 'google_drive'; appOrigin: string }, { authorizationUrl: string }>('startOAuthFlow')({ workspaceId, provider: 'google_drive', appOrigin: window.location.origin })
  return window.open(result.data.authorizationUrl, 'timelining-drive-oauth', 'popup,width=560,height=720')
}

export const listDriveFolders = async (connectionId: string) => {
  const result = await callable<{ connectionId: string }, { files: Array<{ id: string; name: string; webViewLink?: string }> }>('listGoogleDriveFolders')({ connectionId })
  return result.data.files ?? []
}

export const syncDriveFolder = async (connectionId: string, folderId: string) => {
  const result = await callable<{ connectionId: string; folderId: string }, { synced: number; unsupportedGoogleNative: number }>('syncGoogleDriveFolder')({ connectionId, folderId })
  return result.data
}

export const getDrivePickerToken = async (connectionId: string) => (
  await callable<{ connectionId: string }, { accessToken: string; expiresInSeconds: number }>('getGoogleDrivePickerToken')({ connectionId })
).data

export const importStorageFileFromDrive = async (input: { connectionId: string; fileId: string }) => (
  await callable<typeof input, { fileId: string; webViewLink: string | null }>('importGoogleDriveFile')(input)
).data

export const syncStorageFileToDrive = async (input: { connectionId: string; fileId: string; folderId: string }) => (await callable<typeof input, { fileId: string; webViewLink: string | null }>('syncStorageFileToGoogleDrive')(input)).data

export const startDropboxOAuth = async (workspaceId: string) => {
  const result = await callable<{ workspaceId: string }, { authorizationUrl: string }>('startDropboxOAuth')({ workspaceId })
  window.location.assign(result.data.authorizationUrl)
}

export const syncStorageFileToDropbox = async (input: { workspaceId: string; fileId: string; folderPath?: string }) => (
  await callable<typeof input, { fileId: string; path: string }>('syncStorageFileToDropbox')(input)
).data

export const revokeConnection = async (connectionId: string) => {
  await callable<{ connectionId: string }, { revoked: boolean }>('revokeStorageConnection')({ connectionId })
}

export const sortStorageFilesNewestFirst = (files: WorkspaceFileMetadata[]) => [...files].sort((left, right) => timestampMillis(right.createdAt) - timestampMillis(left.createdAt))
